<template>
  <div>
    <CButton color="link" class="px-0 mb-2" @click="cancel">
      <CIcon name="cil-arrow-left" /> Bank accounts
    </CButton>

    <!-- Page Header -->
    <CCard class="mb-4 border">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? (obj.accountName || obj.name || "Bank account") : "Add bank account" }}
              <CBadge v-if="obj.id" :color="getStatusColor(obj.status || 'Active')" class="ml-2 align-middle">
                {{ obj.status || 'Active' }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{ obj.id ? obj.name : "Add a bank or cash account to your business" }}
            </p>
          </div>
          <CDropdown
            v-if="obj.id && obj.status !== 'Inactive'"
            placement="bottom-end"
            toggler-text="More"
            color="light"
          >
            <CDropdownItem @click="deactivateAccount">
              <CIcon name="cil-ban" class="mr-2" /> Deactivate account
            </CDropdownItem>
          </CDropdown>
        </div>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="8">
        <CCard class="mb-4 border">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Account details</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <CRow>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Bank *
                  </template>
                  <template #input>
                    <CSelect
                      :value="obj.name"
                      :options="bankOptions"
                      @change="onBankNameChange"
                      required
                      :invalid="submitted && !obj.name"
                    />
                    <CInvalidFeedback v-if="submitted && !obj.name">
                      Choose the bank
                    </CInvalidFeedback>
                  </template>
                </CFormGroup>
              </CCol>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Account type
                  </template>
                  <template #input>
                    <CSelect
                      :value="obj.accountType"
                      :options="accountTypeOptions"
                      @change="onAccountTypeChange"
                    />
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>
            
            <CRow>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    {{ isCashAccount ? 'Cash account name' : 'Account name' }} *
                  </template>
                  <template #input>
                    <CInput
                      v-model="obj.accountName"
                      :placeholder="isCashAccount ? 'e.g., Office Petty Cash, Sales Counter Cash' : 'Account holder name'"
                      required
                      :invalid="submitted && !obj.accountName"
                    />
                    <CInvalidFeedback v-if="submitted && !obj.accountName">
                      Enter the account name
                    </CInvalidFeedback>
                    <small v-if="isCashAccount" class="form-text text-muted">
                      Name this cash account for easy identification (e.g., Warehouse Cash, Marketing Petty Cash)
                    </small>
                  </template>
                </CFormGroup>
              </CCol>
              <CCol md="6" v-if="!isCashAccount">
                <CFormGroup>
                  <template #label>
                    Account number *
                  </template>
                  <template #input>
                    <CInput
                      v-model="obj.accountNo"
                      placeholder="Account number"
                      required
                      :invalid="submitted && !obj.accountNo"
                    />
                    <CInvalidFeedback v-if="submitted && !obj.accountNo">
                      Enter the account number
                    </CInvalidFeedback>
                  </template>
                </CFormGroup>
              </CCol>
              <CCol md="6" v-if="isCashAccount">
                <CFormGroup>
                  <template #label>
                    Reference no. (optional)
                  </template>
                  <template #input>
                    <CInput
                      v-model="obj.accountNo"
                      placeholder="Internal reference (optional)"
                    />
                    <small class="form-text text-muted">
                      Optional internal reference for this cash account
                    </small>
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Current balance
                  </template>
                  <template #input>
                    <CInput
                      v-model="obj.balance"
                      type="number"
                      step="0.01"
                      inputmode="decimal"
                      placeholder="0.00"
                      prepend="RM"
                    />
                    <small class="form-text text-muted">
                      The balance today, as shown by your bank
                    </small>
                  </template>
                </CFormGroup>
              </CCol>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Currency
                  </template>
                  <template #input>
                    <CSelect
                      :value="obj.currency"
                      :options="currencyOptions"
                      @change="onCurrencyChange"
                    />
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Status
                  </template>
                  <template #input>
                    <CSelect
                      :value="obj.status"
                      :options="statusOptions"
                      @change="onStatusChangeRequest"
                      :disabled="isLoading"
                    />
                    <small v-if="obj.id" class="form-text text-muted">
                      You'll be asked to confirm, and the change is saved straight away
                    </small>
                  </template>
                </CFormGroup>
              </CCol>
              <CCol md="6">
                <CFormGroup>
                  <template #label>
                    Low balance alert
                  </template>
                  <template #input>
                    <CInput
                      v-model="obj.minBalance"
                      type="number"
                      step="0.01"
                      inputmode="decimal"
                      placeholder="0.00"
                      prepend="RM"
                    />
                    <small class="form-text text-muted">
                      The account is flagged as low balance below this amount
                    </small>
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CFormGroup>
              <template #label>
                Notes
              </template>
              <template #input>
                <CTextarea
                  v-model="obj.notes"
                  placeholder="Additional notes about this account"
                  rows="3"
                />
              </template>
            </CFormGroup>
          </CCardBody>
          <CCardFooter class="bg-transparent d-flex justify-content-between p-4">
            <CButton color="light" @click="cancel" :disabled="isLoading">
              Cancel
            </CButton>
            <CButton color="primary" @click="submit" :disabled="isLoading">
              <span v-if="isLoading" class="spinner-border spinner-border-sm mr-2"></span>
              {{ isLoading ? 'Saving…' : (obj.id ? "Save" : "Add account") }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>

      <!-- Sidebar -->
      <CCol lg="4">
        <!-- Account Summary -->
        <CCard class="mb-4 border">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Summary</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <div v-if="obj.id">
              <div class="d-flex align-items-center mb-3">
                <div class="c-avatar c-avatar-lg mr-3 d-flex align-items-center justify-content-center bg-light">
                  <CIcon v-if="isCashAccount" name="cil-money" class="text-muted" size="lg" />
                  <img v-else-if="getBankLogo(obj.name)" :src="getBankLogo(obj.name)" :alt="obj.name" class="img-fluid p-2" style="max-height: 40px;" />
                  <CIcon v-else name="cil-bank" class="text-muted" size="lg" />
                </div>
                <div>
                  <div class="font-weight-bold">{{ obj.name || '—' }}</div>
                  <small class="text-muted">{{ obj.accountType || 'Business Account' }}</small>
                </div>
              </div>

              <div class="mb-3">
                <div class="text-muted small">Current balance</div>
                <div class="font-weight-bold h4 mb-1" :class="{ 'text-danger': Number(obj.balance) < 0 }">
                  {{ formatCurrency(obj.balance || 0) }}
                </div>
                <CBadge v-if="isLowBalance" color="warning">
                  <CIcon name="cil-warning" size="sm" class="mr-1" />Low balance
                </CBadge>
              </div>

              <div class="mb-0">
                <div class="text-muted small">Statements uploaded</div>
                <div class="font-weight-bold">
                  {{ documentList.length }} statement{{ documentList.length === 1 ? '' : 's' }}
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-4">
              <CIcon name="cil-info" size="lg" class="mb-2" />
              <p class="mb-0">The balance and statements show here after you add the account.</p>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Bank Statements Section -->
    <CRow v-if="obj.id">
      <CCol lg="12">
        <CCard class="mb-4 border">
          <CCardHeader class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4">
            <h5 class="mb-0">Bank statements</h5>
            <CButton v-if="documentList.length > 0" color="primary" variant="outline" @click="addNewStatementPopup()">
              <CIcon name="cil-plus" /> Upload statement
            </CButton>
          </CCardHeader>
          <CCardBody class="p-4">
            <!-- Statements: card list below lg -->
            <div v-if="documentList.length > 0" class="d-lg-none">
              <div
                v-for="item in computedDocumentList"
                :key="item.id"
                class="border rounded p-3 mb-2 d-flex justify-content-between align-items-center"
              >
                <div class="mr-2 text-truncate">
                  <CLink :href="item.documentUrl" target="_blank">{{ item.documentName }}</CLink>
                  <div class="small text-muted">{{ item.dateDisplay }}</div>
                </div>
                <div class="d-flex flex-shrink-0">
                  <CButton color="light" class="mr-1" aria-label="Edit statement" @click="onEdit(item)">
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton color="light" aria-label="Delete statement" @click="removeDocumentConfirmation(item)">
                    <CIcon name="cil-trash" />
                  </CButton>
                </div>
              </div>
            </div>
            <!-- Statements Table -->
            <div v-if="documentList.length > 0" class="d-none d-lg-block">
              <CDataTable
                :items="computedDocumentList"
                :fields="documentFields"
                striped
                hover
                responsive
                :items-per-page="10"
                pagination
              >
                <template #show_index="{ index }">
                  <td class="py-2 text-muted">
                    {{ index + 1 }}
                  </td>
                </template>

                <template #document_link="{ item }">
                  <td class="py-2">
                    <div class="d-flex align-items-center">
                      <CIcon 
                        :name="isPdf(item.document) ? 'cil-file-text' : 'cil-image'" 
                        class="text-primary mr-2" 
                      />
                      <CLink :href="item.documentUrl" target="_blank" class="text-primary">
                        {{ item.documentName }}
                      </CLink>
                    </div>
                    <small class="text-muted d-block">
                      {{ getFileSize(item.document) }}
                    </small>
                  </td>
                </template>

                <template #dateDisplay="{ item }">
                  <td class="py-2">
                    <div>{{ item.dateDisplay }}</div>
                  </td>
                </template>

                <template #actions="{ item }">
                  <td class="py-2">
                    <CButtonGroup size="sm">
                      <CButton color="light" @click="viewStatement(item)" title="View" aria-label="View statement" class="border">
                        <CIcon name="cil-eye" />
                      </CButton>
                      <CButton color="light" @click="onEdit(item)" title="Edit" aria-label="Edit statement" class="border">
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CButton color="light" @click="downloadStatement(item)" title="Download" aria-label="Download statement" class="border">
                        <CIcon name="cil-cloud-download" />
                      </CButton>
                      <CButton
                        color="light"
                        class="border"
                        @click="removeDocumentConfirmation(item)"
                        title="Delete"
                        aria-label="Delete statement"
                      >
                        <CIcon name="cil-trash" class="text-danger" />
                      </CButton>
                    </CButtonGroup>
                  </td>
                </template>
              </CDataTable>
            </div>

            <!-- Empty State -->
            <div v-if="documentList.length === 0" class="text-center py-5">
              <CIcon name="cil-file" size="2xl" class="text-muted mb-3" />
              <h5>No statements yet</h5>
              <p class="text-muted mb-3">
                Upload a monthly statement so you can check this account against your records.
              </p>
              <CButton color="primary" @click="addNewStatementPopup()">
                <CIcon name="cil-plus" /> Upload statement
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Add/Edit Statement Modal -->
    <CModal
      :title="newStatement.id ? 'Edit statement' : 'Upload statement'"
      :show.sync="addNewStatementPopupState"
      size="xl"
      @update:show="onAddNewStatementConfirmation"
    >
      <CForm>
        <CFormGroup>
          <template #label>Statement date *</template>
          <template #input>
            <CInput
              type="date"
              :value="computeNewStatementDate"
              @change="setNewStatementDate"
              required
            />
          </template>
        </CFormGroup>

        <CFormGroup>
          <template #label>Statement name (optional)</template>
          <template #input>
            <CInput
              v-model="newStatement.name"
              placeholder="e.g. October 2026"
            />
          </template>
        </CFormGroup>

        <CRow>
          <CCol sm="3">Document *</CCol>
          <CCol sm="9">
            <!-- Existing Document Preview -->
            <CRow v-if="newStatement.document.id != null">
              <CCol sm="12">
                <div class="border rounded p-3 mb-3">
                  <div v-if="isPdf(newStatement.document)">
                    <iframe
                      :src="getDocumentUrl(newStatement.document)"
                      width="100%"
                      height="400px"
                    ></iframe>
                  </div>
                  <div v-else>
                    <CImg
                      :src="getDocumentUrl(newStatement.document)"
                      width="100%"
                      responsive
                      class="rounded"
                    />
                  </div>
                  <div class="mt-2 d-flex justify-content-between align-items-center">
                    <CLink
                      :href="getDocumentUrl(newStatement.document)"
                      target="_blank"
                      class="text-primary"
                    >
                      <CIcon name="cil-external-link" /> Open in new tab
                    </CLink>
                    <CButton
                      color="light"
                      size="sm"
                      @click="removeNewStatementDocumentConfirmation()"
                    >
                      <CIcon name="cil-x" /> Choose another file
                    </CButton>
                  </div>
                </div>
              </CCol>
            </CRow>
            
            <!-- Upload Component -->
            <WidgetsUploadImage
              :billImageUrl="newStatementUrl"
              @uploaded="uploaded_newStatement"
            />
          </CCol>
        </CRow>
      </CForm>
      
      <template #footer>
        <CButton color="light" :disabled="statementSaving" @click="addNewStatementPopupState = false">
          Cancel
        </CButton>
        <CButton color="primary" @click="saveStatement" :disabled="!canSaveStatement || statementSaving">
          {{ statementSaving ? "Saving…" : (newStatement.id ? "Save" : "Upload statement") }}
        </CButton>
      </template>
    </CModal>

    <!-- Delete Statement Confirmation -->
    <CModal
      title="Delete this statement?"
      :show.sync="removeDocumentWarningModal"
      @update:show="onRemoveDocumentConfirmation"
    >
      <p class="mb-2" v-if="documentToDelete">
        The statement dated {{ formatDate(documentToDelete.date) }}
        ({{ getCollectionDocumentName(documentToDelete) }}) and its file will be removed from this
        account. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="removeDocumentWarningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDeleteStatement">
          {{ deleting ? "Deleting…" : "Delete statement" }}
        </CButton>
      </template>
    </CModal>

    <!-- Status Change Confirmation -->
    <CModal
      :title="`Mark this account as ${(pendingStatusChange || '').toLowerCase()}?`"
      :show.sync="statusChangeModal"
      @update:show="onStatusChangeModalClose"
    >
      <div class="mb-2">
        <div class="bg-light p-3 rounded">
          <CRow class="align-items-center">
            <CCol xs="5" class="text-right">
              <CBadge :color="getStatusColor(obj.status)" class="px-3 py-2">
                {{ obj.status || 'Active' }}
              </CBadge>
            </CCol>
            <CCol xs="2" class="text-center">
              <CIcon name="cil-arrow-right" />
            </CCol>
            <CCol xs="5" class="text-left">
              <CBadge :color="getStatusColor(pendingStatusChange)" class="px-3 py-2">
                {{ pendingStatusChange }}
              </CBadge>
            </CCol>
          </CRow>
        </div>
        <p class="mt-3 mb-0">
          {{ getStatusChangeMessage(pendingStatusChange) }} The change is saved straight away.
        </p>
      </div>
      <template #footer>
        <CButton color="light" :disabled="isLoading" @click="cancelStatusChange">
          Cancel
        </CButton>
        <CButton
          :color="getStatusChangeButtonColor(pendingStatusChange)"
          :disabled="isLoading"
          @click="confirmStatusChange"
        >
          Mark as {{ (pendingStatusChange || '').toLowerCase() }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import BankApi from "@/lib/bankApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage.vue";
import { formatDate, mytToday, asDateString, parseDateInput } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

export default {
  name: "Bank",
  components: {
    WidgetsUploadImage,
  },
  data: () => {
    return {
      isLoading: false,
      statementSaving: false,
      uploadOpenedFromQuery: false,
      deleting: false,
      submitted: false,
      newStatement: {
        id: null,
        date: "",
        name: "",
        documentId: null,
        document: {
          id: null,
          fileName: null,
          contentType: null,
        },
      },
      addNewStatementPopupState: false,
      documentToDelete: null,
      removeDocumentWarningModal: false,
      statusChangeModal: false,
      pendingStatusChange: null,
      previousStatus: null,
      
      // Bank options (Malaysian banks)
      bankOptions: [
        { value: "Cash", label: "Cash" },
        { value: "Petty Cash", label: "Petty cash" },
        { value: "Affin Bank Berhad", label: "Affin Bank Berhad" },
        { value: "Alliance Bank Malaysia Berhad", label: "Alliance Bank Malaysia Berhad" },
        { value: "AmBank (M) Berhad", label: "AmBank (M) Berhad" },
        { value: "Bank Islam Malaysia Berhad", label: "Bank Islam Malaysia Berhad" },
        { value: "Bank Kerjasama Rakyat Malaysia Berhad", label: "Bank Kerjasama Rakyat Malaysia Berhad" },
        { value: "Bank Muamalat Malaysia Berhad", label: "Bank Muamalat Malaysia Berhad" },
        { value: "Bank of China (Malaysia) Berhad", label: "Bank of China (Malaysia) Berhad" },
        { value: "Bank Pembangunan Malaysia Berhad", label: "Bank Pembangunan Malaysia Berhad" },
        { value: "Bank Pertanian Malaysia Berhad (Agrobank)", label: "Bank Pertanian Malaysia Berhad (Agrobank)" },
        { value: "Bank Simpanan Nasional", label: "Bank Simpanan Nasional" },
        { value: "CIMB Bank Berhad", label: "CIMB Bank Berhad" },
        { value: "Citibank Berhad", label: "Citibank Berhad" },
        { value: "Deutsche Bank (Malaysia) Berhad", label: "Deutsche Bank (Malaysia) Berhad" },
        { value: "Export-Import Bank of Malaysia Berhad", label: "Export-Import Bank of Malaysia Berhad" },
        { value: "Hong Leong Bank Berhad", label: "Hong Leong Bank Berhad" },
        { value: "HSBC Bank Malaysia Berhad", label: "HSBC Bank Malaysia Berhad" },
        { value: "J.P. Morgan Chase Bank Berhad", label: "J.P. Morgan Chase Bank Berhad" },
        { value: "Malayan Banking Berhad", label: "Malayan Banking Berhad" },
        { value: "Mizuho Bank (Malaysia) Berhad", label: "Mizuho Bank (Malaysia) Berhad" },
        { value: "OCBC Bank (Malaysia) Berhad", label: "OCBC Bank (Malaysia) Berhad" },
        { value: "Public Bank Berhad", label: "Public Bank Berhad" },
        { value: "RHB Bank Berhad", label: "RHB Bank Berhad" },
        { value: "Small Medium Enterprise Development Bank Malaysia Berhad", label: "Small Medium Enterprise Development Bank Malaysia Berhad" },
        { value: "Standard Chartered Bank Malaysia Berhad", label: "Standard Chartered Bank Malaysia Berhad" },
        { value: "Sumitomo Mitsui Banking Corporation Malaysia Berhad", label: "Sumitomo Mitsui Banking Corporation Malaysia Berhad" },
        { value: "United Overseas Bank (Malaysia) Bhd", label: "United Overseas Bank (Malaysia) Bhd" }
      ],
      
      // Account type options
      accountTypeOptions: [
        { value: "Current Account", label: "Current Account" },
        { value: "Savings Account", label: "Savings Account" },
        { value: "Business Account", label: "Business Account" },
        { value: "Fixed Deposit", label: "Fixed Deposit" },
        { value: "Credit Account", label: "Credit Account" },
        { value: "Investment Account", label: "Investment Account" }
      ],
      
      // Currency options
      currencyOptions: [
        { value: "MYR", label: "MYR - Malaysian Ringgit" },
        { value: "USD", label: "USD - US Dollar" },
        { value: "EUR", label: "EUR - Euro" },
        { value: "SGD", label: "SGD - Singapore Dollar" },
        { value: "GBP", label: "GBP - British Pound" },
        { value: "JPY", label: "JPY - Japanese Yen" },
        { value: "AUD", label: "AUD - Australian Dollar" },
        { value: "CAD", label: "CAD - Canadian Dollar" }
      ],

      // Status options
      statusOptions: [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
        { value: "Closed", label: "Closed" },
        { value: "Suspended", label: "Suspended" }
      ],
      
      documentList: [],
      documentFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        { 
          key: "dateDisplay", 
          label: "Date", 
          sorter: true, 
          filter: false 
        },
        {
          key: "document_link",
          label: "Document",
          sorter: false,
          filter: false,
        },
        {
          key: "actions",
          label: "Actions",
          _style: "width:200px",
          sorter: false,
          filter: false,
        },
      ],
      obj: {},
      api: new BankApi(),
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "Bank Accounts", this.obj.id ? "Edit" : "Add"]);
    this.resetObj();
  },
  computed: {
    newStatementUrl() {
      return apiUrl + "documents/file/" + this.newStatement.documentId;
    },
    computeNewStatementDate() {
      return asDateString(this.newStatement.date);
    },
    isLowBalance() {
      const min = Number(this.obj.minBalance) || 0;
      return min > 0 && Number(this.obj.balance || 0) < min;
    },
    computedDocumentList() {
      return this.documentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
          dateDisplay: this.formatDate(item.date),
        };
      });
    },
    canSaveStatement() {
      return this.newStatement.date && this.newStatement.document.id;
    },
    isCashAccount() {
      return this.obj.name === 'Cash' || this.obj.name === 'Petty Cash';
    },
  },
  methods: {
    // Select change handlers
    onBankNameChange(value) {
      // Extract string value from whatever format CSelect provides
      if (typeof value === 'string') {
        this.obj.name = value;
      } else if (value && value.value) {
        this.obj.name = value.value;
      } else if (value && value.target && value.target.value) {
        this.obj.name = value.target.value;
      }

      // Auto-set Account Type to "Cash Account" when Cash or Petty Cash is selected
      if (this.obj.name === 'Cash' || this.obj.name === 'Petty Cash') {
        this.obj.accountType = 'Cash Account';
        // Optional: Clear account number for cash accounts
        if (!this.obj.id) { // Only clear for new accounts, not when editing
          this.obj.accountNo = '';
        }
      }
    },
    onAccountTypeChange(value) {
      // Extract string value from whatever format CSelect provides
      if (typeof value === 'string') {
        this.obj.accountType = value;
      } else if (value && value.value) {
        this.obj.accountType = value.value;
      } else if (value && value.target && value.target.value) {
        this.obj.accountType = value.target.value;
      }
    },
    onCurrencyChange(value) {
      // Extract string value from whatever format CSelect provides
      if (typeof value === 'string') {
        this.obj.currency = value;
      } else if (value && value.value) {
        this.obj.currency = value.value;
      } else if (value && value.target && value.target.value) {
        this.obj.currency = value.target.value;
      }
    },
    onStatusChange(value) {
      // Extract string value from whatever format CSelect provides
      if (typeof value === 'string') {
        this.obj.status = value;
      } else if (value && value.value) {
        this.obj.status = value.value;
      } else if (value && value.target && value.target.value) {
        this.obj.status = value.target.value;
      }
    },
    onStatusChangeRequest(value) {
      // Extract the new status value
      let newStatus = '';
      if (typeof value === 'string') {
        newStatus = value;
      } else if (value && value.value) {
        newStatus = value.value;
      } else if (value && value.target && value.target.value) {
        newStatus = value.target.value;
      }

      // A new account isn't saved yet, so there is nothing to confirm.
      if (!this.obj.id) {
        this.obj.status = newStatus;
        return;
      }

      // Store current status and pending change
      this.previousStatus = this.obj.status;
      this.pendingStatusChange = newStatus;

      // Show confirmation modal
      this.statusChangeModal = true;
    },
    confirmStatusChange() {
      // Apply the status change. Clear previousStatus first so closing the modal doesn't revert it.
      this.obj.status = this.pendingStatusChange;
      this.previousStatus = this.obj.status;
      this.statusChangeModal = false;

      // Save changes automatically
      this.onSubmit();
    },
    cancelStatusChange() {
      // Revert to previous status
      this.obj.status = this.previousStatus;
      this.statusChangeModal = false;
      this.pendingStatusChange = null;
      this.previousStatus = null;
    },
    onStatusChangeModalClose(show) {
      if (!show) {
        this.cancelStatusChange();
      }
    },
    getStatusChangeButtonColor(status) {
      // Closing an account is the only destructive transition.
      return status === 'Closed' ? 'danger' : 'primary';
    },
    getStatusChangeMessage(status) {
      const messages = {
        'Active': 'You can use this account for transactions again.',
        'Inactive': 'The account is hidden from new transactions. You can reactivate it later.',
        'Closed': 'The account is closed and can no longer be used for transactions.',
        'Suspended': 'Transactions on this account may be restricted until you reactivate it.'
      };
      return messages[status] || 'The account status changes.';
    },

    // Utility Methods
    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return value < 0 ? `-RM ${formatMyr(-value)}` : `RM ${formatMyr(value)}`;
    },
    formatDate(dateString) {
      return formatDate(dateString) || '—';
    },
    getBankLogo(bankName) {
      const bankLogos = {
        'Malayan Banking Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Maybank_Logo.svg/1200px-Maybank_Logo.svg.png',
        'CIMB Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/CIMB_Group_logo.svg/1200px-CIMB_Group_logo.svg.png',
        'Public Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Public_Bank_logo.svg/1200px-Public_Bank_logo.svg.png',
        'Hong Leong Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Hong_Leong_Bank_logo.svg/1200px-Hong_Leong_Bank_logo.svg.png',
        'RHB Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/RHB_Bank_logo.svg/1200px-RHB_Bank_logo.svg.png'
      };
      return bankLogos[bankName] || null;
    },
    // UI-GUIDELINES 3.3 `bankAccount`; Suspended needs someone to act, so warning.
    getStatusColor(status) {
      const statusColors = {
        'Active': 'success',
        'Inactive': 'secondary',
        'Closed': 'secondary',
        'Suspended': 'warning'
      };
      return statusColors[status] || 'success';
    },
    isPdf(document) {
      try {
        return document.contentType === "application/pdf";
      } catch (error) {
        return false;
      }
    },
    getDocumentUrl(document) {
      try {
        return apiUrl + "documents/file/" + document.id;
      } catch (error) {
        return "";
      }
    },
    getFileSize(document) {
      // This would need to come from the API
      return document?.fileSize ? this.formatFileSize(document.fileSize) : 'Unknown size';
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    // Statement Management
    removeNewStatementDocumentConfirmation() {
      this.newStatement.documentId = null;
      this.newStatement.document.contentType = null;
      this.newStatement.document.id = null;
    },
    setNewStatementDate(e) {
      this.newStatement.date = parseDateInput(e);
    },
    addNewStatementPopup() {
      this.resetNewStatement();
      this.addNewStatementPopupState = true;
    },
    resetNewStatement() {
      this.newStatement.id = null;
      this.newStatement.name = "";
      this.newStatement.date = mytToday();
      this.newStatement.documentId = null;
      this.newStatement.document = {
        id: null,
        fileName: null,
        contentType: null,
      };
    },
    saveStatement() {
      this.onAddNewStatementConfirmation(true, null, true);
    },
    onAddNewStatementConfirmation(status, evt, accept) {
      if (accept) {
        if (this.statementSaving) return;
        let statementData = { ...this.newStatement };

        // Clean up data
        if (statementData.id == null) delete statementData.id;
        if (statementData.document.id == null) delete statementData.document;
        if (statementData.documentId == null) delete statementData.documentId;

        const isUpdate = !!statementData.id;
        const request = isUpdate
          ? this.api.updateStatement(this.obj.id, statementData)
          : this.api.addStatement(this.obj.id, statementData);
        this.statementSaving = true;
        request
          .then(() => {
            this.$toast.success(isUpdate ? "Statement saved" : "Statement uploaded");
            this.addNewStatementPopupState = false;
            this.resetObj();
          })
          .catch(({ data } = {}) => {
            this.$toast.error("Couldn't save the statement. " + helper.getErrorMessage(data, "Check your connection and try again."));
          })
          .finally(() => {
            this.statementSaving = false;
          });
      } else {
        this.addNewStatementPopupState = false;
      }
    },
    onEdit(item) {
      this.newStatement = { ...item, date: asDateString(item.date) };
      this.addNewStatementPopupState = true;
    },
    viewStatement(item) {
      window.open(this.getCollectionDocumentUrl(item), '_blank');
    },
    downloadStatement(item) {
      const link = document.createElement('a');
      link.href = this.getCollectionDocumentUrl(item);
      link.download = this.getCollectionDocumentName(item);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    removeDocumentConfirmation(item) {
      this.documentToDelete = item;
      this.removeDocumentWarningModal = true;
    },
    confirmDeleteStatement() {
      this.onRemoveDocumentConfirmation(true, null, true);
    },
    onRemoveDocumentConfirmation(status, evt, accept) {
      if (accept && this.documentToDelete) {
        if (this.deleting) return;
        this.deleting = true;
        this.api
          .removeBankStatementDocuments(
            this.documentToDelete.id,
            this.documentToDelete.documentId
          )
          .then(() => {
            this.$toast.success("Statement deleted");
            this.removeDocumentWarningModal = false;
            this.documentToDelete = null;
            this.resetObj();
          })
          .catch(({ data } = {}) => {
            this.$toast.error("Couldn't delete the statement. " + helper.getErrorMessage(data, "Check your connection and try again."));
          })
          .finally(() => {
            this.deleting = false;
          });
      } else if (!this.deleting) {
        this.removeDocumentWarningModal = false;
        this.documentToDelete = null;
      }
    },
    getCollectionDocumentName(item) {
      if (!item.document) return "Statement";
      return item.document.fileName || `Statement_${this.formatDate(item.date)}`;
    },
    getCollectionDocumentUrl(item) {
      return apiUrl + "documents/file/" + item.documentId;
    },
    uploaded_newStatement(data) {
      const uploadedFiles = data.uploadedFiles;
      if (uploadedFiles.length > 0) {
        const file = uploadedFiles[0];
        this.newStatement.documentId = file.id;
        this.newStatement.document.contentType = file.contentType;
        this.newStatement.document.id = file.id;
        this.newStatement.document.fileName = file.fileName;
      }
    },
    
    // Main CRUD Operations
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.documentList = this.obj.bankStatements || [];
            // "Upload statement" from the list opens the upload dialog straight away.
            if (this.$route.query.action === "upload" && !this.uploadOpenedFromQuery) {
              this.uploadOpenedFromQuery = true;
              this.addNewStatementPopup();
            }
          })
          .catch(({ data } = {}) => {
            console.error("Bank account load failed", data);
            this.$toast.error("Couldn't load this bank account. Refresh the page to try again.");
          });
      } else {
        this.obj = this.getEmptyObj();
        this.documentList = [];
      }
    },
    onSubmit() {
      this.submitted = true;

      // Validation - Account Number is optional for Cash accounts
      const isAccountNoRequired = !this.isCashAccount;
      if (!this.obj.name || !this.obj.accountName || (isAccountNoRequired && !this.obj.accountNo)) {
        // Inline field errors show via `submitted`; no toast for validation.
        return;
      }
      if (this.isLoading) return;

      // Ensure all select fields are strings, not objects
      if (this.obj.accountType && typeof this.obj.accountType === 'object') {
        if (this.obj.accountType.value) {
          this.obj.accountType = this.obj.accountType.value;
        } else {
          this.obj.accountType = 'Business Account'; // Default fallback
        }
      }
      if (this.obj.currency && typeof this.obj.currency === 'object') {
        if (this.obj.currency.value) {
          this.obj.currency = this.obj.currency.value;
        } else {
          this.obj.currency = 'MYR'; // Default fallback
        }
      }
      if (this.obj.status && typeof this.obj.status === 'object') {
        if (this.obj.status.value) {
          this.obj.status = this.obj.status.value;
        } else {
          this.obj.status = 'Active'; // Default fallback
        }
      }
      if (this.obj.name && typeof this.obj.name === 'object') {
        if (this.obj.name.value) {
          this.obj.name = this.obj.name.value;
        }
      }

      this.isLoading = true;

      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.$toast.success("Bank account added");
            this.$router.push({ path: `/tenants/Bank/${response.result.id}` });
          })
          .catch(({ data } = {}) => {
            this.$toast.error("Couldn't add the bank account. " + helper.getErrorMessage(data, "Check your connection and try again."));
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then(() => {
            this.$toast.success("Bank account saved");
            this.resetObj();
          })
          .catch(({ data } = {}) => {
            this.$toast.error("Couldn't save the bank account. " + helper.getErrorMessage(data, "Check your connection and try again."));
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    submit() {
      this.onSubmit();
    },
    cancel() {
      this.$router.push({ path: "/tenants/bankList" });
    },
    getEmptyObj() {
      return {
        name: "Malayan Banking Berhad", // Default
        accountName: "",
        accountNo: "",
        accountType: "Business Account",
        balance: 0,
        currency: "MYR",
        status: "Active",
        minBalance: 0,
        notes: "",
      };
    },
    
    deactivateAccount() {
      // Goes through the same confirm as the status select (UI-GUIDELINES 5.4).
      this.onStatusChangeRequest("Inactive");
    },
  },
};
</script>

<style scoped>
/* Statement preview frame */
iframe {
  border: 1px solid var(--light, currentColor);
  border-radius: 0.25rem;
}

.invalid-feedback {
  display: block;
}
</style>
